import { Trans, useLingui } from '@lingui/react/macro'
import faceImage from '$src/assets/images/face.png'
import {
  Button,
  Center,
  Group,
  Image,
  Paper,
  PinInput,
  rem,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { IconArrowLeft } from '@tabler/icons-react'
import { useEffect, useRef } from 'react'
import { Locale, setLocale } from '../../lib/i18n'
import { trpcClient } from '../../lib/trpc'
import { authHooks } from '../../lib/trpcHooks'
import { useAuthStore } from '../../store/useAuthStore'
import { getAttributionPayload } from '../../utils/utm'

interface OtpStepProps {
  email: string
  onBack: () => void
  onRetry: () => void
}

export function OtpStep({ email, onBack, onRetry }: OtpStepProps) {
  const { t } = useLingui()
  const otpInputRef = useRef<HTMLInputElement>(null)
  const { setUser } = useAuthStore()

  const form = useForm({
    initialValues: {
      token: '',
    },
    validate: {
      token: (value) => (value.length === 6 ? null : t`Code must be 6 digits`),
    },
  })

  const verifyOtp = authHooks.useVerifyOtp()

  const handleVerify = async () => {
    const validation = form.validate()
    if (validation.hasErrors) return

    try {
      await verifyOtp.mutateAsync({
        email,
        token: form.values.token,
        app: 'resident',
        attribution: getAttributionPayload(),
      })
      // Auth successful - fetch user data and set in store
      // IndexPage handles redirect via ?redirect param
      const userData = await trpcClient.user.me.query()
      setLocale(userData.uiLanguage as Locale)
      setUser(userData)
    } catch {
      // Error handled by trpcHooks
      form.setFieldValue('token', '')
    }
  }

  useEffect(() => {
    // Focus on OTP input when component mounts
    setTimeout(() => {
      otpInputRef.current?.focus()
    }, 300)
  }, [])

  return (
    <Paper
      radius="xl"
      p="xl"
      withBorder
      shadow="xl"
      style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        width: '100%',
      }}
    >
      <Stack>
        <Group justify="center" mb="md">
          <Image
            src={faceImage}
            alt="Markku"
            style={{
              width: rem(80),
              height: rem(80),
              objectFit: 'contain',
            }}
          />
        </Group>

        <Title order={3} ta="center" mb="xs">
          <Trans>Enter verification code</Trans>
        </Title>
        <Text size="sm" c="dimmed" ta="center" mb="xs">
          <Trans>We sent a 6-digit code to:</Trans>
        </Text>
        <Text size="sm" fw={500} ta="center" mb="lg" c="brandOrange.6">
          {email}
        </Text>

        <Center mb="xl">
          <PinInput
            length={6}
            type="number"
            oneTimeCode
            value={form.values.token}
            onChange={(value) => form.setFieldValue('token', value)}
            onComplete={handleVerify}
            ref={otpInputRef}
            error={!!form.errors.token}
            aria-label={t`Login code`}
            styles={{
              input: {
                fontSize: rem(20),
                fontWeight: 600,
                width: rem(42),
                height: rem(48),
              },
            }}
          />
        </Center>

        <Group justify="space-between" mt="xl">
          <Button
            size="lg"
            radius="xl"
            variant="subtle"
            onClick={onBack}
            leftSection={<IconArrowLeft size={20} />}
          >
            <Trans>Back</Trans>
          </Button>
          <Button
            size="lg"
            radius="xl"
            color="brandOrange"
            onClick={handleVerify}
            loading={verifyOtp.isPending}
            disabled={form.values.token.length !== 6}
          >
            <Trans>Log in</Trans>
          </Button>
        </Group>

        <Center mt="md">
          <Button variant="subtle" size="sm" onClick={onRetry} c="brandBlue.6">
            <Trans>Resend code</Trans>
          </Button>
        </Center>
      </Stack>
    </Paper>
  )
}
